import React, { DragEvent, useState } from "react";
import { Box, Typography, Button, Stack, Alert } from "@mui/material";
import { PhotoCamera, VideoCameraBack } from "@mui/icons-material";

const UploadButton = ({
    label,
    icon,
    accept,
    maxSize,
    onFilesSelected,
    onError,
}: {
    label: string;
    icon: React.ReactNode;
    accept: string;
    maxSize: number;
    onFilesSelected?: (files: File[]) => void;
    onError?: (message: string) => void;
}) => {
    const [dragActive, setDragActive] = useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }
        const files = Array.from(event.target.files);
        console.log("Files selected:", files);

        const invalidFiles: { file: File; reason: string }[] = [];
        const validFiles = Array.from(files).filter((file) => {
            // const isValidType = accept.split(",").some((type) => file.type.startsWith(type.replace("*", "").trim()));
            const isValidSize = file.size <= maxSize;

            // if (!isValidType) {
            //     invalidFiles.push({ file, reason: "invalid type" });
            //     return false;
            // }
            if (!isValidSize) {
                invalidFiles.push({ file, reason: "too large" });
                return false;
            }
            return true;
        });

        console.log("Valid files", validFiles);

        // Handle invalid files
        if (invalidFiles.length > 0) {
            const tooLargeFiles = invalidFiles.filter((f) => f.reason === "too large");
            const invalidTypeFiles = invalidFiles.filter((f) => f.reason === "invalid type");

            let errorMessage = "";
            if (tooLargeFiles.length > 0) {
                const maxSizeMB = maxSize / (1024 * 1024);
                const fileNames = tooLargeFiles.map((f) => f.file.name).join(", ");
                errorMessage += `File(s) too large (max ${maxSizeMB}MB): ${fileNames}`;
            }
            if (invalidTypeFiles.length > 0) {
                if (errorMessage) errorMessage += ". ";
                const fileNames = invalidTypeFiles.map((f) => f.file.name).join(", ");
                errorMessage += `Invalid file type: ${fileNames}`;
            }

            onError?.(errorMessage);
        }

        if (validFiles.length > 0) {
            onFilesSelected?.(validFiles);
        }
    };

    const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        if (!event.dataTransfer) {
            return;
        }

        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            const files = Array.from(event.dataTransfer.files);

            const invalidFiles: { file: File; reason: string }[] = [];
            const validFiles = files.filter((file) => {
                // const isValidType = accept
                //     .split(",")
                //     .some((type) => file.type.startsWith(type.replace("*", "").trim()));
                const isValidSize = file.size <= maxSize;

                // if (!isValidType) {
                //     invalidFiles.push({ file, reason: "invalid type" });
                //     return false;
                // }
                if (!isValidSize) {
                    invalidFiles.push({ file, reason: "too large" });
                    return false;
                }
                return true;
            });

            // Handle invalid files
            if (invalidFiles.length > 0) {
                const tooLargeFiles = invalidFiles.filter((f) => f.reason === "too large");
                const invalidTypeFiles = invalidFiles.filter((f) => f.reason === "invalid type");

                let errorMessage = "";
                if (tooLargeFiles.length > 0) {
                    const maxSizeMB = maxSize / (1024 * 1024);
                    const fileNames = tooLargeFiles.map((f) => f.file.name).join(", ");
                    errorMessage += `File(s) too large (max ${maxSizeMB}MB): ${fileNames}`;
                }
                if (invalidTypeFiles.length > 0) {
                    if (errorMessage) errorMessage += ". ";
                    const fileNames = invalidTypeFiles.map((f) => f.file.name).join(", ");
                    errorMessage += `Invalid file type: ${fileNames}`;
                }

                onError?.(errorMessage);
            }

            if (validFiles.length > 0) {
                onFilesSelected?.(validFiles);
            }
        }
    };

    return (
        <Box textAlign="center" maxWidth={"45%"}>
            <Button
                component="label"
                variant="outlined"
                sx={{
                    width: "12rem",
                    height: "12rem",
                    flexDirection: "column",
                    borderRadius: "1rem",
                    borderStyle: "solid",
                    borderTopColor: "black",
                    borderLeftColor: "black",
                    borderRightColor: "black",
                    borderBottomColor: "black",
                    borderColor: dragActive ? "primary.main" : "grey.400",
                    backgroundColor: dragActive ? "grey.100" : "transparent",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                    transition: "background-color 0.3s, border-color 0.3s",
                    cursor: "pointer",
                    color: "black",
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}>
                {icon}
                <Typography variant="body2" mt={1}>
                    {label}
                </Typography>
                <input
                    type="file"
                    hidden
                    accept={accept}
                    onClick={(e) => {
                        // Reset input value so onChange always fires
                        (e.target as HTMLInputElement).value = "";
                    }}
                    onChange={handleChange}
                    multiple
                />
            </Button>
            <Typography variant="caption" display="block" mt={1}>
                Max&nbsp;
                {Math.floor(maxSize / (1024 * 1024)) < 1024
                    ? `${maxSize / (1024 * 1024)} MB`
                    : `${maxSize / (1024 * 1024 * 1024)} GB`}
            </Typography>
        </Box>
    );
};

interface MediaUploadSectionProps {
    label: string | React.ReactNode;
    maxVideoSize?: number;
    maxImageSize?: number;
    onFilesSelected?: (files: File[]) => void;
}

export const MediaUploadSection = ({
    label,
    maxVideoSize = parseInt(`${process.env.REACT_APP_MAX_VIDEO_SIZE_MB ?? 100}`) * 1024 * 1024, // 100MB
    maxImageSize = parseInt(`${process.env.REACT_APP_MAX_IMG_SIZE_MB ?? 10}`) * 1024 * 1024, // 10MB
    onFilesSelected,
}: MediaUploadSectionProps) => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleError = (message: string) => {
        setErrorMessage(message);
        // Auto-dismiss after 6 seconds
        setTimeout(() => setErrorMessage(null), 6000);
    };

    const handleFilesSelected = (files: File[]) => {
        // Clear any previous errors when valid files are selected
        setErrorMessage(null);
        onFilesSelected?.(files);
    };

    return (
        <Box p={2}>
            {label}
            {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}
            <Stack direction="row" spacing={4} maxWidth={"100%"}>
                <UploadButton
                    label="Upload Image"
                    icon={<PhotoCamera fontSize="large" />}
                    accept="image/*"
                    maxSize={maxImageSize}
                    onFilesSelected={handleFilesSelected}
                    onError={handleError}
                />
                <UploadButton
                    label="Upload Video"
                    icon={<VideoCameraBack fontSize="large" />}
                    accept="*/*"
                    maxSize={maxVideoSize}
                    onFilesSelected={handleFilesSelected}
                    onError={handleError}
                />
            </Stack>
        </Box>
    );
};
